import { supabase } from '../lib/supabase';
import paymentConfirmationService from './paymentConfirmationService';

export const stripePaymentService = {
  async createStripeCustomer(companyData) {
    const { data, error } = await supabase?.functions?.invoke('create-stripe-customer', {
      body: {
        companyId: companyData?.id,
        email: companyData?.contactEmail,
        name: companyData?.companyName,
        phone: companyData?.contactPhone,
        address: companyData?.companyAddress
      }
    });

    if (error) throw error;
    return data;
  },

  async getStripeCustomer(companyId) {
    const { data, error } = await supabase
      ?.from('stripe_customers')
      ?.select('*')
      ?.eq('company_id', companyId)
      ?.single();

    if (error && error?.code !== 'PGRST116') throw error;
    return data;
  },

  async createPaymentIntent(invoiceData, customerInfo) {
    const { data, error } = await supabase?.functions?.invoke('create-payment-intent', {
      body: {
        invoiceData,
        customerInfo
      }
    });

    if (error) throw error;
    return data;
  },

  async confirmPayment(paymentIntentId) {
    const { data, error } = await supabase?.functions?.invoke('confirm-payment', {
      body: { paymentIntentId }
    });

    if (error) throw error;

    // Send payment confirmation email
    try {
      if (data?.paymentIntent && data?.transaction) {
        const invoice = await this.getInvoiceById(data?.transaction?.stripe_invoice_id);
        const company = await this.getCompanyById(data?.transaction?.company_id);
        
        if (invoice && company) {
          await paymentConfirmationService?.sendStripePaymentConfirmation(
            data?.paymentIntent,
            invoice,
            company
          );
        }
      }
    } catch (emailError) {
      console.error('Failed to send payment confirmation email:', emailError);
      // Don't throw - payment succeeded even if email failed
    }

    return data;
  },

  async getPaymentMethods(companyId) {
    const { data, error } = await supabase
      ?.from('stripe_payment_methods')?.select('*')?.eq('company_id', companyId)?.order('is_default', { ascending: false })
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(method => ({
      id: method?.id,
      stripePaymentMethodId: method?.stripe_payment_method_id,
      type: method?.type,
      cardBrand: method?.card_brand,
      cardLastFour: method?.card_last_four,
      cardExpMonth: method?.card_exp_month,
      cardExpYear: method?.card_exp_year,
      isDefault: method?.is_default,
      createdAt: method?.created_at
    })) || [];
  },

  async addPaymentMethod(companyId, paymentMethodData) {
    const { data, error } = await supabase?.functions?.invoke('add-payment-method', {
      body: {
        companyId,
        paymentMethodData
      }
    });

    if (error) throw error;
    return data;
  },

  async removePaymentMethod(paymentMethodId) {
    const { data, error } = await supabase?.functions?.invoke('remove-payment-method', {
      body: { paymentMethodId }
    });

    if (error) throw error;
    return data;
  },

  async setDefaultPaymentMethod(companyId, paymentMethodId) {
    const { error } = await supabase
      ?.from('stripe_payment_methods')
      ?.update({ is_default: false })
      ?.eq('company_id', companyId);

    if (error) throw error;

    const { data, error: updateError } = await supabase
      ?.from('stripe_payment_methods')
      ?.update({ is_default: true })
      ?.eq('id', paymentMethodId)
      ?.select()
      ?.single();

    if (updateError) throw updateError;
    return data;
  },

  async getInvoices(companyId) {
    const { data, error } = await supabase
      ?.from('stripe_invoices')?.select(`*,subscription_billing_history:subscription_billing_history_id (tier_at_billing,employee_count_at_billing)`)?.eq('company_id', companyId)?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(invoice => ({
      id: invoice?.id,
      stripeInvoiceId: invoice?.stripe_invoice_id,
      invoiceNumber: invoice?.invoice_number,
      status: invoice?.status,
      amountDue: invoice?.amount_due,
      amountPaid: invoice?.amount_paid,
      currency: invoice?.currency,
      billingPeriodStart: invoice?.billing_period_start,
      billingPeriodEnd: invoice?.billing_period_end,
      dueDate: invoice?.due_date,
      paidAt: invoice?.paid_at,
      hostedInvoiceUrl: invoice?.hosted_invoice_url,
      invoicePdfUrl: invoice?.invoice_pdf_url,
      tierAtBilling: invoice?.subscription_billing_history?.tier_at_billing,
      employeeCountAtBilling: invoice?.subscription_billing_history?.employee_count_at_billing,
      createdAt: invoice?.created_at
    })) || [];
  },

  async getPaymentTransactions(companyId) {
    const { data, error } = await supabase
      ?.from('payment_transactions')?.select(`*,stripe_invoice:stripe_invoice_id (invoice_number,amount_due)`)?.eq('company_id', companyId)?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(transaction => ({
      id: transaction?.id,
      stripePaymentIntentId: transaction?.stripe_payment_intent_id,
      stripeChargeId: transaction?.stripe_charge_id,
      amount: transaction?.amount,
      currency: transaction?.currency,
      status: transaction?.status,
      errorMessage: transaction?.error_message,
      invoiceNumber: transaction?.stripe_invoice?.invoice_number,
      createdAt: transaction?.created_at
    })) || [];
  },

  async createInvoiceForBilling(billingHistoryId, companyId) {
    const { data, error } = await supabase?.functions?.invoke('create-subscription-invoice', {
      body: {
        billingHistoryId,
        companyId
      }
    });

    if (error) throw error;
    return data;
  },

  async getInvoiceById(invoiceId) {
    const { data, error } = await supabase
      ?.from('stripe_invoices')?.select(`*,subscription_billing_history:subscription_billing_history_id (tier_at_billing,employee_count_at_billing)`)?.eq('id', invoiceId)
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      stripeInvoiceId: data?.stripe_invoice_id,
      invoiceNumber: data?.invoice_number,
      status: data?.status,
      amountDue: data?.amount_due,
      amountPaid: data?.amount_paid,
      currency: data?.currency,
      billingPeriodStart: data?.billing_period_start,
      billingPeriodEnd: data?.billing_period_end,
      dueDate: data?.due_date,
      paidAt: data?.paid_at,
      hostedInvoiceUrl: data?.hosted_invoice_url,
      invoicePdfUrl: data?.invoice_pdf_url,
      tierAtBilling: data?.subscription_billing_history?.tier_at_billing,
      employeeCountAtBilling: data?.subscription_billing_history?.employee_count_at_billing
    };
  },

  async getCompanyById(companyId) {
    const { data, error } = await supabase
      ?.from('company_settings')
      ?.select('*')
      ?.eq('id', companyId)
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      companyName: data?.company_name,
      companyLegalName: data?.company_legal_name,
      contactEmail: data?.contact_email,
      contactPhone: data?.contact_phone,
      companyAddress: data?.company_address
    };
  },

  formatAmount(amount, currency = 'PGK') {
    return new Intl.NumberFormat('en-PG', {
      style: 'currency',
      currency: currency,
      currencyDisplay: 'code'
    })?.format(amount)?.replace('PGK', 'K');
  },

  getStatusColor(status) {
    const statusColors = {
      'paid': 'text-success bg-success/10',
      'open': 'text-warning bg-warning/10',
      'draft': 'text-muted-foreground bg-muted',
      'void': 'text-error bg-error/10',
      'uncollectible': 'text-error bg-error/10'
    };
    return statusColors?.[status] || 'text-muted-foreground bg-muted';
  },

  getPaymentStatusColor(status) {
    const statusColors = {
      'succeeded': 'text-success bg-success/10',
      'processing': 'text-warning bg-warning/10',
      'pending': 'text-warning bg-warning/10',
      'failed': 'text-error bg-error/10',
      'cancelled': 'text-muted-foreground bg-muted',
      'refunded': 'text-primary bg-primary/10'
    };
    return statusColors?.[status] || 'text-muted-foreground bg-muted';
  }
};

export default stripePaymentService;